import type { Segment } from '../types'
import type { EvidenceRow } from '../ensemble/types'

type Stimulus = { stimulus_id: string; label: string; content: string; type: string }

// Deterministic seed — always same demo data
export const DEMO_PROJECT_ID = 'proj_insurance_demo'
export const DEMO_PANEL_ID = 'panel_insurance_demo'
export const DEMO_TENANT_ID = 'tnt_demo'
export const DEMO_WORKSPACE_ID = 'wks_demo'

export const DEMO_STIMULI: Array<{ stimulus_id: string; label: string; content: string; type: string }> = [
  {
    stimulus_id: 'stim_a',
    label: 'Message A',
    content: 'Your premium has increased due to market conditions affecting all policyholders in your area.',
    type: 'message',
  },
  {
    stimulus_id: 'stim_b',
    label: 'Message B',
    content: 'Your renewal premium reflects the actual cost of claims paid in your area last year — we explain exactly what changed and why.',
    type: 'message',
  },
  {
    stimulus_id: 'stim_c',
    label: 'Message C',
    content: 'We value your loyalty. Your updated premium includes enhanced coverage options selected for your home profile.',
    type: 'message',
  },
]

export const DEMO_SEGMENTS: Segment[] = [
  {
    segment_id: 'seg_homeowners',
    tenant_id: DEMO_TENANT_ID,
    workspace_id: DEMO_WORKSPACE_ID,
    project_id: DEMO_PROJECT_ID,
    upload_id: 'upload_insurance_demo',
    name: 'General Homeowners',
    respondent_count: 842,
    demographic_skew: { age_median: 44, income_band: '$75k–$125k', home_type: 'single-family' },
    dominant_themes: [
      'Trust and transparency in pricing',
      'Concrete explanation reduces anxiety',
      'Market-conditions language feels dismissive',
    ],
    evidence_strength: 'High',
    representative_quotes: [
      { text: 'If they explain exactly what changed and why, I\'m more likely to stay. Vague answers make me shop around.', segment: 'General Homeowners', source_file: 'insurance-renewal-survey.csv', source_row: 47 },
      { text: 'I just want to understand the number. Give me the breakdown and I\'ll accept it.', segment: 'General Homeowners', source_file: 'insurance-renewal-survey.csv', source_row: 112 },
      { text: '"Market conditions" is not an explanation. That\'s a non-answer.', segment: 'General Homeowners', source_file: 'insurance-renewal-survey.csv', source_row: 203 },
    ],
    created_at: 1700000000000,
  },
  {
    segment_id: 'seg_claimants',
    tenant_id: DEMO_TENANT_ID,
    workspace_id: DEMO_WORKSPACE_ID,
    project_id: DEMO_PROJECT_ID,
    upload_id: 'upload_insurance_demo',
    name: 'Recent Claimants',
    respondent_count: 156,
    demographic_skew: { age_median: 48, claim_recency_months: 8, claim_type: 'property' },
    dominant_themes: [
      'Feeling heard after a difficult claim experience',
      'Need for claims-process acknowledgment',
      'Skepticism about renewal framing post-claim',
    ],
    evidence_strength: 'Low',
    representative_quotes: [
      { text: 'I just went through a stressful claim. I need to know you see me as a person, not a number.', segment: 'Recent Claimants', source_file: 'insurance-renewal-survey.csv', source_row: 891 },
      { text: 'After my claim, I expected some kind of acknowledgment. None of these messages does that.', segment: 'Recent Claimants', source_file: 'insurance-renewal-survey.csv', source_row: 934 },
    ],
    created_at: 1700000000000,
  },
  {
    segment_id: 'seg_price_sensitive',
    tenant_id: DEMO_TENANT_ID,
    workspace_id: DEMO_WORKSPACE_ID,
    project_id: DEMO_PROJECT_ID,
    upload_id: 'upload_insurance_demo',
    name: 'Price-Sensitive Renters',
    respondent_count: 248,
    demographic_skew: { age_median: 31, income_band: '$35k–$65k', tenure_years: 2 },
    dominant_themes: [
      'Pure cost comparison behavior',
      'Low loyalty, high churn risk',
      'Responds to competitor benchmarks',
    ],
    evidence_strength: 'Medium',
    representative_quotes: [
      { text: 'I compare every year. If you\'re more than $50 higher than competitors, I switch.', segment: 'Price-Sensitive Renters', source_file: 'insurance-renewal-survey.csv', source_row: 642 },
    ],
    created_at: 1700000000000,
  },
]

export const DEMO_EVIDENCE: EvidenceRow[] = [
  { respondent_id: 'R001', segment: 'General Homeowners', response: 'Message B', open_text: 'If they explain exactly what changed and why, I\'m more likely to stay. Vague answers make me shop around.', source_file: 'insurance-renewal-survey.csv', row_index: 47 },
  { respondent_id: 'R002', segment: 'General Homeowners', response: 'Message B', open_text: 'I just want to understand the number. Give me the breakdown and I\'ll accept it.', source_file: 'insurance-renewal-survey.csv', row_index: 112 },
  { respondent_id: 'R003', segment: 'General Homeowners', response: 'Message A', open_text: '"Market conditions" is not an explanation. That\'s a non-answer.', source_file: 'insurance-renewal-survey.csv', row_index: 203 },
  { respondent_id: 'R004', segment: 'General Homeowners', response: 'Message B', open_text: 'Appreciate the transparency. Makes me feel like they\'re treating me like an adult.', source_file: 'insurance-renewal-survey.csv', row_index: 278 },
  { respondent_id: 'R005', segment: 'General Homeowners', response: 'Message B', open_text: 'Concrete numbers build trust. I hate vague corporate speak.', source_file: 'insurance-renewal-survey.csv', row_index: 341 },
  { respondent_id: 'R006', segment: 'General Homeowners', response: 'Message C', open_text: 'The loyalty message feels hollow without a real explanation.', source_file: 'insurance-renewal-survey.csv', row_index: 412 },
  { respondent_id: 'R007', segment: 'General Homeowners', response: 'Message B', open_text: 'When I understand why the price went up, I accept it more easily.', source_file: 'insurance-renewal-survey.csv', row_index: 489 },
  { respondent_id: 'R008', segment: 'Recent Claimants', response: 'Message B', open_text: 'I just went through a stressful claim. I need to know you see me as a person, not a number.', source_file: 'insurance-renewal-survey.csv', row_index: 891 },
  { respondent_id: 'R009', segment: 'Recent Claimants', response: 'Message A', open_text: 'After my claim, I expected some kind of acknowledgment. None of these messages does that.', source_file: 'insurance-renewal-survey.csv', row_index: 934 },
  { respondent_id: 'R010', segment: 'Recent Claimants', response: 'Message B', open_text: 'The explanation helps but I still feel like the claims experience isn\'t acknowledged.', source_file: 'insurance-renewal-survey.csv', row_index: 967 },
  { respondent_id: 'R011', segment: 'Price-Sensitive Renters', response: 'Message A', open_text: 'I compare every year. If you\'re more than $50 higher than competitors, I switch.', source_file: 'insurance-renewal-survey.csv', row_index: 642 },
  { respondent_id: 'R012', segment: 'Price-Sensitive Renters', response: 'Message B', open_text: 'At least Message B gives me something to compare. Message A just sounds like an excuse.', source_file: 'insurance-renewal-survey.csv', row_index: 701 },
  { respondent_id: 'R013', segment: 'General Homeowners', response: 'Message B', open_text: 'This is the kind of communication that builds long-term loyalty.', source_file: 'insurance-interview-notes.txt', row_index: 3 },
  { respondent_id: 'R014', segment: 'General Homeowners', response: 'Message B', open_text: 'Previous insurer used vague language and I left. Specific language keeps me around.', source_file: 'insurance-interview-notes.txt', row_index: 7 },
  { respondent_id: 'R015', segment: 'Recent Claimants', response: 'Message C', open_text: 'The "enhanced coverage" framing is a bit manipulative after a claim — feels like upselling.', source_file: 'insurance-interview-notes.txt', row_index: 14 },
]

export const DEMO_PRE_READ_QUESTION = 'Which renewal message creates the most trust and renewal intent?'

export const DEMO_COLUMN_CLASSIFICATIONS = [
  { column_name: 'respondent_id', category: 'respondent_id', confidence: 'High', sample_values: ['R001', 'R002', 'R003'] },
  { column_name: 'age', category: 'demographic', confidence: 'High', sample_values: ['34', '52', '28'] },
  { column_name: 'segment', category: 'demographic', confidence: 'High', sample_values: ['General Homeowners', 'Recent Claimants', 'Price-Sensitive Renters'] },
  { column_name: 'income_band', category: 'demographic', confidence: 'High', sample_values: ['$75k–$125k', '$35k–$65k', '$125k+'] },
  { column_name: 'renewal_preference', category: 'prediction', confidence: 'High', sample_values: ['Message B', 'Message A', 'Message B'] },
  { column_name: 'renewal_open', category: 'open_ended', confidence: 'High', sample_values: ['If they explain exactly...', 'I compare every year...', 'The loyalty message...'] },
  { column_name: 'renewed', category: 'outcome', confidence: 'Medium', sample_values: ['1', '0', '1'] },
  { column_name: 'survey_date', category: 'ignore', confidence: 'High', sample_values: ['2024-01-15', '2024-01-16', '2024-01-15'] },
]
